import { lazy } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { seoHead } from "@/config/seoHead";
import {
  compareHeading,
  hasCompareParams,
  parseCompareParams,
  pickCompareSearch,
  type CompareSummary,
} from "@/lib/compareLink";

const JamforElavtal = lazy(() => import("@/pages/elavtal/JamforElavtal"));
const JamforResultat = lazy(() => import("@/pages/elavtal/JamforResultat"));

type CompareSearch = ReturnType<typeof pickCompareSearch>;

/**
 * /elavtal/jamfor – jämförelsesidan med sökformulär.
 *
 * Med parametrar (?omrade=SE3&forbrukning=20000[&postnummer=…][&avtalstyp=…])
 * blir den en delbar resultatsida: samma resultat som sökflödet (/sokresultat)
 * utan formulär. Parametersidor är noindex med canonical till /elavtal.
 */
export const Route = createFileRoute("/elavtal/jamfor")({
  // Övriga parametrar (t.ex. utm_*) lämnas orörda i URL:en; jämförelsen läser
  // bara sina egna nycklar via pickCompareSearch().
  validateSearch: (search: Record<string, unknown>): CompareSearch & Record<string, unknown> => ({
    ...search,
  }),
  loaderDeps: ({ search }) => pickCompareSearch(search),
  loader: async ({ deps }): Promise<{ summary: CompareSummary | null }> => {
    const params = parseCompareParams(deps);
    // Sammanfattningen hämtas bara vid serverrendering – i webbläsaren laddar
    // sökresultatkomponenten själv (med skrapskyddet).
    if (!params || typeof document !== "undefined") return { summary: null };
    try {
      const [{ loadOfferResults }, { summarizeResults }] = await Promise.all([
        import("@/hooks/useOfferResults"),
        import("@/lib/compareLink"),
      ]);
      const results = await loadOfferResults({
        elomrade: params.area,
        contractType: null,
        segment: null,
        consumption: params.consumption,
      });
      return { summary: summarizeResults(results, params) };
    } catch (err) {
      console.error("Jämförelsen kunde inte hämtas i loadern", err);
      return { summary: null };
    }
  },
  head: ({ match }) => {
    const search = (match.search ?? {}) as Record<string, unknown>;
    if (!hasCompareParams(search)) return seoHead("/elavtal/jamfor");
    const params = parseCompareParams(search);
    return seoHead("/elavtal/jamfor", {
      ...(params
        ? {
            title: `${compareHeading(params)} | Elmarknad.se`,
            description: `Jämför ${compareHeading(params).replace(/^Elavtal/, "elavtal")} – jämförpris och villkor från Sveriges elbolag.`,
          }
        : {}),
      noindex: true,
      canonicalPath: "/elavtal",
    });
  },
  component: JamforRoute,
});

function JamforRoute() {
  const search = Route.useSearch();
  const { summary } = Route.useLoaderData();
  const params = parseCompareParams(search);
  return params ? <JamforResultat params={params} summary={summary} /> : <JamforElavtal />;
}
